import { lazy, ComponentType } from 'react';

const RETRY_KEY = 'chunk_load_retry';

/**
 * Wrapper for React.lazy that handles chunk load failures
 * by clearing cache and reloading the page
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const hasRetried = sessionStorage.getItem(RETRY_KEY);

    try {
      const component = await importFn();
      // Clear retry flag on success
      sessionStorage.removeItem(RETRY_KEY);
      return component;
    } catch (error) {
      // Check if this is a chunk load error
      const isChunkError = 
        error instanceof Error && 
        (error.name === 'ChunkLoadError' || 
         error.message.includes('Loading chunk') ||
         error.message.includes('Failed to fetch dynamically imported module') ||
         error.message.includes('Failed to load'));

      if (isChunkError && !hasRetried) {
        console.warn('[lazyWithRetry] Chunk load failed, clearing cache and reloading...');
        sessionStorage.setItem(RETRY_KEY, 'true');
        
        // Clear service worker cache
        if ('caches' in window) {
          try {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          } catch (e) {
            console.warn('[lazyWithRetry] Failed to clear caches:', e);
          }
        }
        
        // Reload the page to get fresh chunks
        window.location.reload();
        
        // Return a never-resolving promise while reloading
        return new Promise(() => {});
      }

      throw error;
    }
  });
}

/**
 * Clear cache and reload the page (for manual chunk error handling)
 */
export async function clearCacheAndReload(): Promise<void> {
  console.warn('[clearCacheAndReload] Clearing cache and reloading...');
  
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (e) {
      console.warn('[clearCacheAndReload] Failed to clear caches:', e);
    }
  }
  
  window.location.reload();
}
